import moment from 'moment-timezone';
import { flux } from '@/core/flux.module';
import type { Timezone } from '@/datetime/timezone.service';
import type { GRID_OPTION, SAMPLES_TABLE_MODE } from '@/tools/exportOData/odata.constants';

type SamplesTableModeValue = (typeof SAMPLES_TABLE_MODE)[keyof typeof SAMPLES_TABLE_MODE];
type GridOptionValue = (typeof GRID_OPTION)[keyof typeof GRID_OPTION];

/**
 * Sets the Samples export mode
 *
 * @param {SAMPLES_TABLE_MODE} samplesMode - Value for samples table mode
 */
export function setSamplesMode(samplesMode: SamplesTableModeValue) {
  flux.dispatch('EXPORT_ODATA_SAMPLES_MODE', { samplesMode });
}

/**
 * Sets whether autoupdate is enabled for the time range
 *
 * @param {boolean} autoupdateTimeRange - True to enable autoupdate
 */
export function setAutoupdateTimeRange(autoupdateTimeRange: boolean) {
  flux.dispatch('EXPORT_ODATA_AUTOUPDATE_TIME_RANGE', { autoupdateTimeRange });
}

/**
 * Set time range start that is used in the export form.
 *
 * @param {moment} start - the start of the time range
 */
export function setTimeRangeStart(start: moment.Moment | Date) {
  flux.dispatch('EXPORT_ODATA_TIME_RANGE_START', { start });
}

/**
 * Set time range end that is used in the export form.
 *
 * @param {moment} end - the end of the time range
 */
export function setTimeRangeEnd(end: moment.Moment | Date) {
  flux.dispatch('EXPORT_ODATA_TIME_RANGE_END', { end });
}

/**
 * Sets the duration of the time range.
 *
 * @param {moment.duration} duration - The duration of the time range
 */
export function setDuration(duration: moment.Moment | number | moment.Duration) {
  let durationValue: moment.Duration;
  if (moment.isDuration(duration)) {
    durationValue = duration;
  } else if (moment.isMoment(duration)) {
    durationValue = moment.duration(duration.valueOf());
  } else {
    durationValue = moment.duration(duration);
  }
  flux.dispatch('EXPORT_ODATA_DURATION', { duration: durationValue });
}

export function setExportTimeZone(exportTimeZone: Timezone) {
  flux.dispatch('EXPORT_ODATA_EXPORT_TIME_ZONE', { exportTimeZone });
}

export function setGridOption(gridOption: GridOptionValue) {
  flux.dispatch('EXPORT_ODATA_GRID_OPTION', { gridOption });
}

export function setGridSize(gridSize: { value: number; units: string }) {
  flux.dispatch('EXPORT_ODATA_GRID_SIZE', { gridSize });
}

export function setGridOrigin(gridOrigin: moment.Moment | Date) {
  flux.dispatch('EXPORT_ODATA_GRID_ORIGIN', { gridOrigin });
}

export function setGridOriginEnabled(gridOriginEnabled: boolean) {
  flux.dispatch('EXPORT_ODATA_GRID_ORIGIN_ENABLED', { gridOriginEnabled });
}

export function setODataId(id: string) {
  flux.dispatch('EXPORT_ODATA_ID', { id });
}

export function setODataWorksheetLink(worksheetLink: string) {
  flux.dispatch('EXPORT_ODATA_WORKSHEET_LINK', { worksheetLink });
}

export function setIncludeTextContext(includeTextContext: boolean) {
  flux.dispatch('EXPORT_ODATA_INCLUDE_TEXT_CONTEXT', { includeTextContext });
}
